import type { ReactNode } from "react";
import { usePortfolioQuery } from "web_myplann/Api/Portfolio/Queries/usePortfolioQuery";
import { usePortfolioRightOptional } from "web_myplann/Api/Rights/RightsProvider";
import type {
	ClientPortfolioDomainEnum,
	PortfolioDetailResponseDTO,
} from "web_myplann/Infrastructure/Api/Api";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import { DetailSectionLoader } from "web_myplann/Portfolio/Components/DetailSectionLoader";
import { MyPlannLayout } from "web_myplann/Shared/Header/MyPlannLayout";
import NavigationHeader from "web_myplann/Shared/Header/NavigationHeader";
import { useParsedParam } from "web_myplann/Shared/Hooks/useParsedParam";
import { useRedirectGuard } from "web_myplann/Shared/Hooks/useRedirectGuard";
import { parseToFiniteNumber } from "web_myplann/Shared/Utils/paramParsers";

type Props = {
	expectedDomain: ClientPortfolioDomainEnum;
	title: string | ((portfolio: PortfolioDetailResponseDTO) => string);
	children: (ctx: {
		portfolio: PortfolioDetailResponseDTO;
		portfolioId: number;
		detailRoute: string;
	}) => ReactNode;
};

export const PortfolioEditLayout = ({
	expectedDomain,
	title,
	children,
}: Props) => {
	const portfolioId = useParsedParam("id", parseToFiniteNumber);

	const { data: portfolio, isLoading } = usePortfolioQuery(portfolioId);

	const detailRoute =
		portfolioId !== undefined
			? Routing.Portfolio.PortfolioDetail.path(String(portfolioId))
			: Routing.Portfolio.PortfolioList.path();

	const hasPortfolioRight = usePortfolioRightOptional();
	useRedirectGuard(!hasPortfolioRight, detailRoute);

	const isExpectedDomain = portfolio?.domain === expectedDomain;
	const canRenderChildren =
		portfolio && isExpectedDomain && portfolioId !== undefined;

	const resolvedTitle =
		typeof title === "function"
			? title(
					(canRenderChildren
						? portfolio
						: undefined) as PortfolioDetailResponseDTO,
				)
			: title;

	return (
		<MyPlannLayout
			disableContentPadding
			topBar={
				<NavigationHeader
					title={resolvedTitle}
					defaultRoute={detailRoute}
					alwaysUseDefaultRoute
				/>
			}
		>
			{isLoading ? (
				<DetailSectionLoader />
			) : canRenderChildren ? (
				children({
					portfolio,
					portfolioId,
					detailRoute,
				})
			) : null}
		</MyPlannLayout>
	);
};
